import { useEffect, useMemo, useState } from 'react';
import {
  LayoutDashboard,
  ShoppingBag,
  UtensilsCrossed,
  TrendingUp,
  LogOut,
  Menu,
  X,
  Bell,
  Users,
  DollarSign,
  Clock,
  Star,
  ArrowUp,
  ArrowDown,
  AlertCircle,
  CheckCircle,
  Settings,
  ChevronRight,
  BarChart3,
} from 'lucide-react';
import type { User } from '../../App';
import { getRestaurants } from '../../lib/mockData';
import { api } from '../../lib/api';
import { OrdersView } from '../restaurant/OrdersView';
import { MenuManagement } from '../restaurant/MenuManagement';
import { RestaurantSettings } from '../restaurant/RestaurantSettings';
import { Analytics } from '../restaurant/Analytics';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { LucideIcon } from 'lucide-react';

interface ManagerDashboardProps {
  user: User;
  onLogout: () => void;
}

type Tab = 'dashboard' | 'orders' | 'menu' | 'reports' | 'settings';
type TimeRange = 'today' | 'week' | 'month';

type NavigationItem = {
  key: Tab;
  label: string;
  Icon: LucideIcon;
};

type DashboardSummary = {
  restaurant: any;
  metrics: {
    totalOrders: number;
    todayOrders: number;
    activeOrders: number;
    pendingOrders: number;
    todayRevenue: number;
    rating: number;
    totalReviews: number;
  };
  menuOverview: {
    totalItems: number;
    activeItems: number;
    categories: number;
  };
  orderStatusCounts: Record<string, number>;
  recentOrders: Array<{
    id: string;
    orderNumber: string;
    status: string;
    total: number;
    createdAt: string;
    customerName: string;
    itemsCount: number;
  }>;
};

type AnalyticsData = {
  metrics: {
    totalRevenue: number;
    totalOrders: number;
    averageOrderValue: number;
    uniqueCustomers: number;
    repeatRate: number;
    completionRate: number;
    activeMenuItems: number;
    revenueGrowth: number;
    orderGrowth: number;
    averageRating: number;
    totalReviews: number;
  };
  charts: {
    revenueTrend: Array<{ name: string; revenue: number; orders: number }>;
    orderStatusDistribution: Array<{ key: string; name: string; value: number; color: string }>;
    peakHours: Array<{ hour: string; orders: number }>;
    topSellingItems: Array<{ id: string; name: string; quantity: number; revenue: number }>;
  };
};

const emptyAnalytics = (): AnalyticsData => ({
  metrics: {
    totalRevenue: 0,
    totalOrders: 0,
    averageOrderValue: 0,
    uniqueCustomers: 0,
    repeatRate: 0,
    completionRate: 0,
    activeMenuItems: 0,
    revenueGrowth: 0,
    orderGrowth: 0,
    averageRating: 0,
    totalReviews: 0,
  },
  charts: {
    revenueTrend: [],
    orderStatusDistribution: [],
    peakHours: [],
    topSellingItems: [],
  },
});

const formatStatus = (status: string) =>
  String(status || 'PENDING')
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const navigationItems: NavigationItem[] = [
  { key: 'dashboard', Icon: LayoutDashboard, label: 'Dashboard' },
  { key: 'orders', Icon: ShoppingBag, label: 'Orders' },
  { key: 'menu', Icon: UtensilsCrossed, label: 'Menu' },
  { key: 'reports', Icon: TrendingUp, label: 'Reports' },
  { key: 'settings', Icon: Settings, label: 'Settings' },
];

export function ManagerDashboard({ user, onLogout }: ManagerDashboardProps) {
  const [activeTab, setActiveTab] = useState<Tab>('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [timeRange, setTimeRange] = useState<TimeRange>('today');
  const [showNotifications, setShowNotifications] = useState(false);
  const [apiRestaurant, setApiRestaurant] = useState<any | null>(null);
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsData>(emptyAnalytics());
  const [staffCount, setStaffCount] = useState(0);
  const [activeStaffCount, setActiveStaffCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [logoLoadFailed, setLogoLoadFailed] = useState(false);

  const restaurants = getRestaurants();
  const resolvedRestaurantId =
    user.restaurantId || (user as any)?.restaurant_id || (user as any)?.restaurant?.id || restaurants[0]?.id || '';
  const restaurant =
    summary?.restaurant ||
    apiRestaurant ||
    restaurants.find((item) => item.id === resolvedRestaurantId) ||
    (resolvedRestaurantId ? { id: resolvedRestaurantId, name: 'Restaurant', openTime: '09:00', closeTime: '22:00' } : null);
  const settingsRestaurant = {
    ...(restaurants.find((item) => item.id === resolvedRestaurantId) || {}),
    ...(summary?.restaurant || {}),
    ...(apiRestaurant || {}),
  };

  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      if (!resolvedRestaurantId) return;
      setLoading(true);
      try {
        const [restaurantResponse, summaryResponse, analyticsResponse, staffResponse] = await Promise.all([
          api.restaurants.getById(String(resolvedRestaurantId)),
          api.restaurants.getDashboardSummary(String(resolvedRestaurantId)),
          api.restaurants.getAnalytics(String(resolvedRestaurantId), { range: timeRange }),
          api.users.getAll({ restaurantId: String(resolvedRestaurantId), limit: 100 }),
        ]);
        if (!mounted) return;
        setApiRestaurant(restaurantResponse?.data || null);
        setSummary(summaryResponse?.data || null);
        setAnalytics(analyticsResponse?.data || emptyAnalytics());
        const staffItems = Array.isArray(staffResponse?.data?.users) ? staffResponse.data.users : [];
        setStaffCount(staffItems.length);
        setActiveStaffCount(staffItems.filter((item: any) => item?.isActive !== false).length);
      } catch {
        if (!mounted) return;
        setAnalytics(emptyAnalytics());
        setSummary(null);
        setStaffCount(0);
        setActiveStaffCount(0);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void loadData();
    return () => {
      mounted = false;
    };
  }, [resolvedRestaurantId, timeRange]);

  useEffect(() => {
    setLogoLoadFailed(false);
  }, [restaurant?.logo, restaurant?.id]);

  const restaurantLogo = String(restaurant?.logo || '').trim();
  const restaurantInitials = String(restaurant?.name || user?.name || 'Restaurant')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const notifications = summary?.recentOrders.filter((order) =>
    ['PENDING', 'CONFIRMED'].includes(String(order.status || '').toUpperCase())
  ) || [];

  const alerts = useMemo(() => {
    const items: Array<{ tone: string; message: string }> = [];
    const inactiveItems = Math.max((summary?.menuOverview.totalItems || 0) - (summary?.menuOverview.activeItems || 0), 0);
    if ((summary?.metrics.pendingOrders || 0) > 0) items.push({ tone: 'yellow', message: `${summary?.metrics.pendingOrders} orders need follow-up` });
    if (inactiveItems > 0) items.push({ tone: 'blue', message: `${inactiveItems} menu items are inactive` });
    if ((analytics.metrics.completionRate || 0) >= 90) items.push({ tone: 'green', message: `Completion rate is ${analytics.metrics.completionRate.toFixed(1)}%` });
    return items.slice(0, 3);
  }, [analytics.metrics.completionRate, summary]);

  if (!restaurant) {
    return <div>No restaurant is configured for this manager account.</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <div className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-gradient-to-b from-green-700 to-green-900 text-white transition-all duration-300 flex flex-col fixed h-full z-40`}>
        <div className="p-4 border-b border-green-600">
          <div className="flex items-center justify-between">
            {sidebarOpen && (
              <div>
                <h2 className="text-white">{restaurant.name}</h2>
                <p className="text-green-200 text-sm">Manager Portal</p>
              </div>
            )}
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-green-600 rounded-lg">
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {navigationItems.map(({ key, Icon, label }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === key ? 'bg-green-600' : 'hover:bg-green-600/50'
              }`}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span>{label}</span>}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-green-600">
          <div className="flex items-center gap-3 mb-3">
            {restaurantLogo && !logoLoadFailed ? (
              <img src={restaurantLogo} alt={restaurant?.name || user.name} className="w-10 h-10 rounded-full object-cover" onError={() => setLogoLoadFailed(true)} />
            ) : (
              <div className="w-10 h-10 rounded-full bg-green-500 text-white flex items-center justify-center font-semibold text-sm uppercase">
                {restaurantInitials || 'RA'}
              </div>
            )}
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm truncate">{user.name}</p>
                <p className="text-green-200 text-xs truncate">{user.email}</p>
              </div>
            )}
          </div>
          <button onClick={onLogout} className="w-full flex items-center gap-3 px-4 py-2 bg-green-600 hover:bg-green-500 rounded-lg transition-colors">
            <LogOut className="w-4 h-4 flex-shrink-0" />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </div>

      <div className={`flex-1 ${sidebarOpen ? 'ml-64' : 'ml-20'} transition-all duration-300`}>
        <div className="p-6 md:p-8">
          {activeTab === 'dashboard' && (
            <div>
              <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                <div>
                  <h1 className="text-gray-900 mb-1">Manager Dashboard</h1>
                  <p className="text-gray-600">Live operational overview for your restaurant</p>
                  {loading && <p className="text-xs text-gray-500 mt-1">Refreshing live dashboard...</p>}
                </div>

                <div className="flex items-center gap-3">
                  <select
                    value={timeRange}
                    onChange={(e) => setTimeRange(e.target.value as TimeRange)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="today">Today</option>
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                  </select>

                  <div className="relative">
                    <button onClick={() => setShowNotifications(!showNotifications)} className="p-2 hover:bg-gray-100 rounded-lg relative">
                      <Bell className="w-6 h-6 text-gray-600" />
                      {notifications.length > 0 && <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />}
                    </button>

                    {showNotifications && (
                      <div className="absolute right-0 mt-2 w-96 overflow-hidden bg-white rounded-xl shadow-2xl border border-green-100 z-50">
                        <div className="p-5 border-b border-green-100 bg-green-50">
                          <div className="flex items-center justify-between">
                            <h3 className="text-lg font-bold text-gray-900">Manager Notifications</h3>
                            <button onClick={() => setShowNotifications(false)} className="p-1 hover:bg-white rounded-lg transition-colors">
                              <X className="w-5 h-5 text-gray-600" />
                            </button>
                          </div>
                          <p className="text-sm text-green-700 mt-2 font-medium">
                            {summary?.metrics.pendingOrders || 0} pending · {summary?.orderStatusCounts?.READY || 0} ready
                          </p>
                        </div>

                        <div className="max-h-96 overflow-y-auto">
                          {notifications.length > 0 ? notifications.slice(0, 5).map((order) => (
                            <button
                              key={order.id}
                              onClick={() => {
                                setShowNotifications(false);
                                setActiveTab('orders');
                              }}
                              className="w-full p-5 border-b border-gray-100 hover:bg-green-50/40 transition-colors text-left"
                            >
                              <div className="flex items-start gap-3">
                                <div className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 ${String(order.status).toUpperCase() === 'PENDING' ? 'bg-orange-100' : 'bg-green-100'}`}>
                                  {String(order.status).toUpperCase() === 'PENDING' ? (
                                    <AlertCircle className="w-5 h-5 text-orange-600" />
                                  ) : (
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between gap-3 mb-1">
                                    <p className="font-semibold text-gray-900 text-sm">{String(order.status).toUpperCase() === 'PENDING' ? 'New Order' : 'Order Confirmed'}</p>
                                    <span className="text-xs text-gray-500 truncate">{order.orderNumber}</span>
                                  </div>
                                  <p className="text-sm text-gray-700 mb-1">{order.customerName} - Rs {order.total}</p>
                                  <p className="text-xs text-gray-500">{order.itemsCount} items - {new Date(order.createdAt).toLocaleTimeString('en-IN')}</p>
                                </div>
                                <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                              </div>
                            </button>
                          )) : (
                            <div className="p-8 text-center text-gray-500">No manager alerts right now</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {alerts.length > 0 && (
                <div className="mb-6 space-y-2">
                  {alerts.map((alert, index) => (
                    <div
                      key={`${alert.message}-${index}`}
                      className={`flex items-center gap-3 p-4 rounded-lg border ${
                        alert.tone === 'yellow' ? 'bg-yellow-50 border-yellow-200' : alert.tone === 'green' ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'
                      }`}
                    >
                      <AlertCircle className={`w-5 h-5 ${alert.tone === 'yellow' ? 'text-yellow-600' : alert.tone === 'green' ? 'text-green-600' : 'text-blue-600'}`} />
                      <p className="text-sm font-medium text-gray-800">{alert.message}</p>
                    </div>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-white/20 rounded-lg"><DollarSign className="w-6 h-6" /></div>
                    <div className="flex items-center gap-1 text-sm bg-white/20 px-2 py-1 rounded-full">
                      {analytics.metrics.revenueGrowth >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                      <span>{Math.abs(analytics.metrics.revenueGrowth).toFixed(1)}%</span>
                    </div>
                  </div>
                  <h3 className="text-white/80 text-sm mb-1">Revenue</h3>
                  <p className="text-3xl font-bold mb-1">Rs {analytics.metrics.totalRevenue.toLocaleString('en-IN')}</p>
                  <p className="text-white/60 text-xs">Avg value: Rs {analytics.metrics.averageOrderValue.toFixed(0)}</p>
                </div>

                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-white/20 rounded-lg"><ShoppingBag className="w-6 h-6" /></div>
                    <div className="flex items-center gap-1 text-sm bg-white/20 px-2 py-1 rounded-full">
                      {analytics.metrics.orderGrowth >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                      <span>{Math.abs(analytics.metrics.orderGrowth).toFixed(1)}%</span>
                    </div>
                  </div>
                  <h3 className="text-white/80 text-sm mb-1">Active Orders</h3>
                  <p className="text-3xl font-bold mb-1">{summary?.metrics.activeOrders || 0}</p>
                  <p className="text-white/60 text-xs">Pending: {summary?.metrics.pendingOrders || 0}</p>
                </div>

                <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-white/20 rounded-lg"><Users className="w-6 h-6" /></div>
                    <div className="flex items-center gap-1 text-sm bg-white/20 px-2 py-1 rounded-full">
                      <CheckCircle className="w-3 h-3" />
                      <span>{activeStaffCount}</span>
                    </div>
                  </div>
                  <h3 className="text-white/80 text-sm mb-1">Staff On Duty</h3>
                  <p className="text-3xl font-bold mb-1">{activeStaffCount} / {staffCount}</p>
                  <p className="text-white/60 text-xs">Restaurant staff</p>
                </div>

                <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg p-6 text-white">
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-white/20 rounded-lg"><UtensilsCrossed className="w-6 h-6" /></div>
                    <div className="flex items-center gap-1 text-sm bg-white/20 px-2 py-1 rounded-full">
                      <AlertCircle className="w-3 h-3" />
                      <span>{Math.max((summary?.menuOverview.totalItems || 0) - (summary?.menuOverview.activeItems || 0), 0)}</span>
                    </div>
                  </div>
                  <h3 className="text-white/80 text-sm mb-1">Active Menu Items</h3>
                  <p className="text-3xl font-bold mb-1">{summary?.menuOverview.activeItems || 0}</p>
                  <p className="text-white/60 text-xs">Total listed: {summary?.menuOverview.totalItems || 0}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-green-500">
                  <p className="text-gray-600 text-xs">Avg Order Value</p>
                  <p className="text-gray-900 text-lg font-semibold">Rs {analytics.metrics.averageOrderValue.toFixed(0)}</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-blue-500">
                  <p className="text-gray-600 text-xs">Completion Rate</p>
                  <p className="text-gray-900 text-lg font-semibold">{analytics.metrics.completionRate.toFixed(1)}%</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-purple-500">
                  <p className="text-gray-600 text-xs">Rating</p>
                  <p className="text-gray-900 text-lg font-semibold">{analytics.metrics.averageRating > 0 ? analytics.metrics.averageRating.toFixed(1) : 'N/A'}</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-orange-500">
                  <p className="text-gray-600 text-xs">Repeat Customers</p>
                  <p className="text-gray-900 text-lg font-semibold">{analytics.metrics.repeatRate.toFixed(1)}%</p>
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-gray-900">Revenue Trend</h3>
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  </div>
                  {analytics.charts.revenueTrend.length === 0 ? (
                    <div className="h-[250px] flex items-center justify-center text-gray-500">No trend data available</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={250}>
                      <AreaChart data={analytics.charts.revenueTrend}>
                        <defs>
                          <linearGradient id="managerRevenue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.25} />
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="name" stroke="#9ca3af" />
                        <YAxis stroke="#9ca3af" />
                        <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }} />
                        <Area type="monotone" dataKey="revenue" stroke="#10b981" fill="url(#managerRevenue)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </div>

                <div className="bg-white rounded-xl shadow-sm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-gray-900">Order Status</h3>
                    <BarChart3 className="w-5 h-5 text-blue-600" />
                  </div>
                  {analytics.charts.orderStatusDistribution.length === 0 ? (
                    <div className="h-[250px] flex items-center justify-center text-gray-500">No order data available</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie data={analytics.charts.orderStatusDistribution} cx="50%" cy="50%" outerRadius={90} dataKey="value">
                          {analytics.charts.orderStatusDistribution.map((entry) => (
                            <Cell key={entry.key} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </div>

                <div className="bg-white rounded-xl shadow-sm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-gray-900">Peak Hours</h3>
                    <Clock className="w-5 h-5 text-orange-600" />
                  </div>
                  {analytics.charts.peakHours.length === 0 ? (
                    <div className="h-[250px] flex items-center justify-center text-gray-500">No peak hour data available</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={analytics.charts.peakHours}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="hour" stroke="#9ca3af" />
                        <YAxis stroke="#9ca3af" />
                        <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }} />
                        <Bar dataKey="orders" fill="#3b82f6" radius={[8, 8, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>

                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="text-gray-900 mb-4">Top Selling Items</h3>
                  <div className="space-y-3">
                    {analytics.charts.topSellingItems.length === 0 && <div className="text-gray-500 py-8 text-center">No item sales data available</div>}
                    {analytics.charts.topSellingItems.slice(0, 5).map((item, index) => (
                      <div key={item.id} className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{index + 1}. {item.name}</p>
                          <p className="text-xs text-gray-500">{item.quantity} sold</p>
                        </div>
                        <p className="text-sm font-semibold text-green-700">Rs {item.revenue.toLocaleString('en-IN')}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-gray-900 mb-4">Recent Orders</h3>
                <OrdersView restaurantId={restaurant.id} compact={true} />
              </div>
            </div>
          )}

          {activeTab === 'orders' && <OrdersView restaurantId={restaurant.id} />}
          {activeTab === 'menu' && <MenuManagement restaurantId={restaurant.id} readOnly={false} />}
          {activeTab === 'reports' && <Analytics restaurantId={restaurant.id} />}
          {activeTab === 'settings' && (
            <RestaurantSettings
              restaurant={settingsRestaurant as any}
              onRestaurantUpdated={(updatedRestaurant) => {
                setApiRestaurant((prev: any) => ({ ...(prev || {}), ...(updatedRestaurant || {}) }));
                setSummary((prev) => prev ? { ...prev, restaurant: { ...prev.restaurant, ...(updatedRestaurant || {}) } } : prev);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
